/*
 * <c4p-opening-board> — Chess4pro. Plays an opening's main line up to the position printed on the mug.
 * No dependencies. Moves come precomputed (UCI + SAN) from the product's chess.* metafields.
 * Piece images: "cburnett" set by Colin M.L. Burnett, licensed GFDL / BSD / GPL (via Wikimedia Commons and lichess.org).
 */
(() => {
  if (customElements.get('c4p-opening-board')) return;
  const START = 'rnbqkbnrpppppppp' + '.'.repeat(32) + 'PPPPPPPPRNBQKBNR';
  const sq = (s) => (8 - Number(s[1])) * 8 + (s.charCodeAt(0) - 97);
  const ES = { N: 'C', B: 'A', R: 'T', Q: 'D', K: 'R' };
  const reduce = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  function replay(uci, n) {
    // returns {cells: [{id, p}|null x64], last: [from,to]|null}
    const cells = [...START].map((p, i) => (p === '.' ? null : { id: 'p' + i, p }));
    let last = null;
    for (let k = 0; k < n; k++) {
      const m = uci[k], f = sq(m.slice(0, 2)), t = sq(m.slice(2, 4)), pc = cells[f];
      if (!pc) break;
      const lower = pc.p.toLowerCase();
      if (lower === 'k' && Math.abs((t % 8) - (f % 8)) === 2) { // castling
        const row = Math.floor(f / 8) * 8, kingside = t % 8 === 6;
        const rf = row + (kingside ? 7 : 0), rt = row + (kingside ? 5 : 3);
        cells[rt] = cells[rf]; cells[rf] = null;
      }
      if (lower === 'p' && f % 8 !== t % 8 && !cells[t]) cells[t + (pc.p === 'P' ? 8 : -8)] = null; // en passant
      cells[t] = pc; cells[f] = null;
      if (m[4]) cells[t] = { id: pc.id + 'q', p: pc.p === 'P' ? m[4].toUpperCase() : m[4] };
      last = [f, t];
    }
    return { cells, last };
  }

  class OpeningBoard extends HTMLElement {
    connectedCallback() {
      if (this._ready) return;
      this._ready = true;
      this.uci = (this.dataset.uci || '').trim().split(/\s+/).filter(Boolean);
      this.san = (this.dataset.san || '').replace(/\d+\.(\.\.)?/g, ' ').trim().split(/\s+/).filter(Boolean);
      this.es = (this.dataset.locale || document.documentElement.lang || 'en').startsWith('es');
      this.black = this.dataset.orientation === 'black';
      this.base = this.dataset.pieces || '';
      this.ply = this.dataset.start === 'end' ? this.uci.length : 0;
      this.render();
      this.go(this.ply, false);
      if (this.hasAttribute('data-autoplay') && !reduce() && this.uci.length) {
        this.go(0, false);
        const io = new IntersectionObserver((es) => {
          if (es.some((e) => e.isIntersecting)) { io.disconnect(); setTimeout(() => this.play(), 500); }
        }, { threshold: 0.5 });
        io.observe(this);
      } else {
        this.go(this.uci.length, false);
      }
    }
    t(en, es) { return this.es ? es : en; }
    label(i) {
      const s = this.san[i] || '';
      const txt = this.es ? s.replace(/[NBRQK]/g, (c) => ES[c]) : s;
      return (i % 2 === 0 ? `${i / 2 + 1}.` : '') + txt;
    }
    render() {
      const name = this.dataset.name || '';
      this.innerHTML = `
        <div class="c4p-ob__board" role="img" aria-label="${this.t('Board', 'Tablero')}: ${name}">
          <div class="c4p-ob__squares">${Array.from({ length: 64 }, (_, i) => `<span class="c4p-ob__sq ${((i % 8) + Math.floor(i / 8)) % 2 ? 'is-dark' : ''}" data-i="${i}"></span>`).join('')}</div>
          <div class="c4p-ob__pieces"></div>
        </div>
        <div class="c4p-ob__controls">
          <button type="button" data-a="first" aria-label="${this.t('Start position', 'Posición inicial')}"><svg viewBox="0 0 24 24" aria-hidden="true"><path d="M6 5v14M18 6l-8 6 8 6z"/></svg></button>
          <button type="button" data-a="prev" aria-label="${this.t('Previous move', 'Jugada anterior')}"><svg viewBox="0 0 24 24" aria-hidden="true"><path d="M15 6l-6 6 6 6"/></svg></button>
          <button type="button" data-a="play" aria-label="${this.t('Play the line', 'Reproducir la línea')}"><svg viewBox="0 0 24 24" aria-hidden="true"><path class="i-play" d="M8 5l11 7-11 7z"/><path class="i-pause" d="M8 5v14M16 5v14"/></svg></button>
          <button type="button" data-a="next" aria-label="${this.t('Next move', 'Jugada siguiente')}"><svg viewBox="0 0 24 24" aria-hidden="true"><path d="M9 6l6 6-6 6"/></svg></button>
          <button type="button" data-a="last" aria-label="${this.t('Final position', 'Posición final')}"><svg viewBox="0 0 24 24" aria-hidden="true"><path d="M18 5v14M6 6l8 6-8 6z"/></svg></button>
        </div>
        <ol class="c4p-ob__moves" aria-label="${this.t('Moves', 'Jugadas')}">${this.san.map((_, i) => `<li><button type="button" data-ply="${i + 1}">${this.label(i)}</button></li>`).join('')}</ol>
        <p class="c4p-ob__live visually-hidden" aria-live="polite"></p>`;
      this.$pieces = this.querySelector('.c4p-ob__pieces');
      this.addEventListener('click', (e) => {
        const b = e.target.closest('button'); if (!b) return;
        if (b.dataset.ply) { this.stop(); return this.go(Number(b.dataset.ply)); }
        const a = b.dataset.a;
        if (a === 'play') return this.timer ? this.stop() : this.play();
        this.stop();
        if (a === 'first') this.go(0); if (a === 'last') this.go(this.uci.length);
        if (a === 'prev') this.go(this.ply - 1); if (a === 'next') this.go(this.ply + 1);
      });
      this.addEventListener('keydown', (e) => {
        if (e.key === 'ArrowLeft') { this.stop(); this.go(this.ply - 1); }
        if (e.key === 'ArrowRight') { this.stop(); this.go(this.ply + 1); }
      });
    }
    pos(i) {
      const v = this.black ? 63 - i : i;
      return `translate(${(v % 8) * 100}%, ${Math.floor(v / 8) * 100}%)`;
    }
    go(n, announce = true) {
      n = Math.max(0, Math.min(this.uci.length, n));
      this.ply = n;
      const { cells, last } = replay(this.uci, n);
      const seen = new Set();
      cells.forEach((c, i) => {
        if (!c) return;
        seen.add(c.id);
        let el = this.$pieces.querySelector(`[data-id="${c.id}"]`);
        if (!el) {
          el = document.createElement('img');
          el.dataset.id = c.id; el.alt = ''; el.width = 45; el.height = 45; el.decoding = 'async';
          el.className = 'c4p-ob__piece';
          this.$pieces.append(el);
        }
        const file = (c.p === c.p.toUpperCase() ? 'w' : 'b') + c.p.toUpperCase();
        if (el.dataset.p !== file) { el.dataset.p = file; el.src = this.base.replace('PIECE', file); }
        el.style.transform = this.pos(i);
      });
      this.$pieces.querySelectorAll('[data-id]').forEach((el) => { if (!seen.has(el.dataset.id)) el.remove(); });
      this.querySelectorAll('.c4p-ob__sq.is-last').forEach((s) => s.classList.remove('is-last'));
      if (last) last.forEach((i) => this.querySelector(`.c4p-ob__sq[data-i="${this.black ? 63 - i : i}"]`).classList.add('is-last'));
      this.querySelectorAll('.c4p-ob__moves button').forEach((b) => b.toggleAttribute('aria-current', Number(b.dataset.ply) === n));
      const cur = this.querySelector('.c4p-ob__moves [aria-current]');
      if (cur && announce) cur.scrollIntoView({ block: 'nearest', inline: 'nearest' });
      if (announce) this.querySelector('.c4p-ob__live').textContent = n ? this.label(n - 1) : this.t('Start position', 'Posición inicial');
      this.classList.toggle('is-end', n === this.uci.length);
    }
    play() {
      if (this.ply >= this.uci.length) this.go(0, false);
      this.querySelector('[data-a="play"]').setAttribute('aria-label', this.t('Pause', 'Pausa'));
      this.classList.add('is-playing');
      this.timer = setInterval(() => {
        if (this.ply >= this.uci.length) return this.stop();
        this.go(this.ply + 1, false);
      }, reduce() ? 1200 : 700);
    }
    stop() {
      clearInterval(this.timer); this.timer = null; this.classList.remove('is-playing');
      const b = this.querySelector('[data-a="play"]'); if (b) b.setAttribute('aria-label', this.t('Play the line', 'Reproducir la línea'));
    }
    disconnectedCallback() { this.stop(); }
  }
  customElements.define('c4p-opening-board', OpeningBoard);
})();
